/**
 * Auflösung der UI-Sprache beim App-Start.
 *
 * Ursprünglich inline in src/index.tsx definiert, hierher extrahiert
 * für bessere Testbarkeit (gleiches Vorgehen wie bei iframeParams.ts).
 * index.tsx importiert resolveLanguage weiterhin; die Persistenz in den
 * Storage sowie die moment-/antd-Locale-Setzung verbleiben in index.tsx.
 *
 * Priorität: ?i18n-Query-Param > gespeicherter Wert ("i18n" im Storage)
 * > navigator.language > "en".
 *
 * Explizite Anredevarianten werden vor allgemeinen Browser-Locale-Präfixen
 * geprüft. Dadurch bleiben `?i18n=de-DU` und `?i18n=de-SIE` erhalten und
 * können ihrer dokumentierten Priorität gegenüber gespeicherten bzw.
 * mandantenseitigen Defaults entsprechen.
 */

export interface ResolveLanguageInput {
    /** Wert des ?i18n-Query-Parameters (höchste Priorität) */
    queryParam?: string
    /** Gespeicherter Wert aus safeStorage.getItem("i18n") */
    stored?: string
    /** window.navigator.language (niedrigste Priorität vor dem Fallback "en") */
    navigatorLanguage?: string
}

// Map browser locale like de-DE to base language 'de'. GraphQL-Enum-Tokens
// (DeDU/DeSIE) werden ebenfalls akzeptiert, damit App und Startauflösung
// dieselbe Normalisierung verwenden.
export const normalizeLanguage = (lng?: string): string | undefined => {
    if (!lng) return undefined
    const lower = lng.toLowerCase()
    if (lower === "dedu" || lower === "de-du" || lower === "de_du") return "de-DU"
    if (lower === "desie" || lower === "de-sie" || lower === "de_sie") return "de-SIE"
    if (lower.startsWith("de")) return "de"
    if (lower.startsWith("en")) return "en"
    return undefined
}

/** Löst die beim App-Start zu verwendende Sprache auf. */
export const resolveLanguage = ({ queryParam, stored, navigatorLanguage }: ResolveLanguageInput): string => {
    return normalizeLanguage(queryParam) || normalizeLanguage(stored || undefined) || normalizeLanguage(navigatorLanguage) || "en"
}

/**
 * Sprache, nachdem die Mandanten-Einstellung geladen ist: Ohne expliziten
 * `?i18n`-Parameter gilt der Mandanten-Default — unabhängig von Browser-Sprache
 * und gespeichertem Wert. Ein expliziter Parameter gewinnt; nur `?i18n=de`
 * wird auf die Anredevariante (du/Sie) des Mandanten verfeinert.
 */
export const applyTenantDefault = ({ queryParam, tenantDefault }: { queryParam?: string; tenantDefault: string }): string => {
    const explicit = normalizeLanguage(queryParam)
    if (!explicit) return tenantDefault
    if (explicit === "de" && tenantDefault.startsWith("de")) return tenantDefault
    return explicit
}
